import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { IStaffTask, isClosed, studentName, IN_PROGRESS } from './taskBuckets';
import * as S from './TaskBoard.styled';

interface ITaskCardProps {
  task: IStaffTask;
  overdue?: boolean;
  onOpen: (task: IStaffTask) => void;
  onToggleDone: (task: IStaffTask) => void;
}

/**
 * Compact board card. The whole card opens the details drawer; the round «✓»
 * is the only quick action (done/reopen) and stops propagation. Group /
 * student / agreement are muted context text — navigation lives in the drawer.
 */
export const TaskCard = memo(
  ({ task, overdue = false, onOpen, onToggleDone }: ITaskCardProps) => {
    const { t } = useTranslation();
    const done = isClosed(task);
    const context = [
      task.group?.name,
      studentName(task),
      task.agreement?.agreementTitle,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <S.CardBox
        role="button"
        tabIndex={0}
        done={done}
        onClick={() => onOpen(task)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onOpen(task);
        }}
      >
        <S.CardCheck
          type="button"
          done={done}
          title={done ? t('pages.tasks.reopen') : t('pages.tasks.markDone')}
          onClick={(e) => {
            e.stopPropagation();
            onToggleDone(task);
          }}
        >
          ✓
        </S.CardCheck>
        <S.CardTitle done={done}>{task.title}</S.CardTitle>
        <S.CardMeta>
          {task.dueDate ? (
            <S.CardDue overdue={overdue}>{task.dueDate}</S.CardDue>
          ) : null}
          {task.priority === 'high' ? (
            <S.Chip tone="warn">{t('pages.tasks.priority.high')}</S.Chip>
          ) : null}
          {task.status === IN_PROGRESS ? (
            <S.Chip tone="accent">
              {t(`pages.tasks.status.${IN_PROGRESS}`)}
            </S.Chip>
          ) : null}
          {task.assigneeName ? <span>{task.assigneeName}</span> : null}
        </S.CardMeta>
        {context ? <S.CardContext>{context}</S.CardContext> : null}
      </S.CardBox>
    );
  },
);

TaskCard.displayName = 'TaskCard';
